import { useState, useRef, useCallback, useEffect } from 'react';

export interface UseDebouncedInputOptions {
  initialValue: string;
  debounceMs?: number;
  onSave: (value: string) => void;
}

export interface UseDebouncedInputResult {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * Hook for debounced input with immediate save on blur.
 * Syncs with external value changes (e.g., collab).
 */
export function useDebouncedInput(options: UseDebouncedInputOptions): UseDebouncedInputResult {
  const { initialValue, debounceMs = 1000, onSave } = options;

  const [value, setValue] = useState(initialValue);
  const timerRef = useRef<number>(0);
  const lastSavedRef = useRef(initialValue);

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = 0;
    }
  };

  // Sync from external changes (e.g., collab)
  // Clear pending timer to prevent stale closure values from overwriting incoming changes
  useEffect(() => {
    clearTimer();
    setValue(initialValue);
    lastSavedRef.current = initialValue;
  }, [initialValue]);

  useEffect(() => clearTimer, []);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setValue(newValue);
      clearTimer();

      timerRef.current = window.setTimeout(() => {
        if (newValue !== lastSavedRef.current) {
          lastSavedRef.current = newValue;
          onSave(newValue);
        }
      }, debounceMs);
    },
    [debounceMs, onSave],
  );

  const onBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      clearTimer();

      if (newValue !== lastSavedRef.current) {
        lastSavedRef.current = newValue;
        onSave(newValue);
      }
    },
    [onSave],
  );

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  }, []);

  return { value, onChange, onBlur, onKeyDown };
}
